import { showFastTrackRegistration } from 'AdmissionOffer';
import { formattedUserFacingMonthDayYearLong } from 'DateHelpers';
import { type CurrentUser, getAdmissionOffer } from 'Users';
import { type CohortRegistrationOption, type CohortRegistrationOptions } from './TuitionAndRegistration.types';

/*
    Offers to a program that supports_registering_into_other_cohorts_on_offer? show more than one
    cohort to register into:
      - with a "fast track" offer: the fast track cohort, the offer cohort, the next upcoming cohort
      - with a normal offer: the offer cohort and the next two upcoming cohorts
    Every admission offer has at least the offer cohort, so the list is never empty.
*/
export function useCohortRegistrationOptions(
    currentUser: CurrentUser,
    classStartDate: string,
): CohortRegistrationOptions {
    const admissionOffer = getAdmissionOffer(currentUser);

    if (!admissionOffer) {
        return {
            cohortRegistrationOptions: [],
            showMultipleCohortRegistrationOptions: false,
            defaultCohortId: null,
        };
    }

    const showFastTrack = showFastTrackRegistration(admissionOffer);

    // We always show the offer cohort.
    const options: CohortRegistrationOption[] = [{ cohortId: admissionOffer.cohortId, date: classStartDate }];

    /*
        The fast track option registers into the prior cohort. Absent unless the program supports it.

        showFastTrackRegistration only checks fastTrackRegistrationDeadline, so an offer can be fast
        track eligible with no fastTrackCohortId, which would give the learner a radio that posts
        cohort_id: null. Healthy offers set both fields together, so skipping it costs nothing.
    */
    if (showFastTrack && admissionOffer.fastTrackCohortId) {
        const fastTrackStartDateTs = admissionOffer.fastTrackStartDate;
        options.push({
            cohortId: admissionOffer.fastTrackCohortId,
            date: fastTrackStartDateTs ? formattedUserFacingMonthDayYearLong(1000 * fastTrackStartDateTs, false) : null,
        });
    }

    // Upcoming cohorts always come after the offer cohort. Also absent unless the program supports it.
    admissionOffer.upcomingCohortDates?.forEach(cohortAttrs => {
        options.push({
            cohortId: cohortAttrs.id,
            date: formattedUserFacingMonthDayYearLong(new Date(cohortAttrs.startDate * 1000), false),
        });
    });

    const cohortRegistrationOptions = options
        .sort((a, b) => (Date.parse(a.date ?? '') < Date.parse(b.date ?? '') ? -1 : 1))
        .slice(0, 3);

    return {
        cohortRegistrationOptions,
        showMultipleCohortRegistrationOptions: cohortRegistrationOptions.length > 1,
        // The earliest start date, falling back to the offer cohort.
        defaultCohortId: cohortRegistrationOptions[0]?.cohortId ?? admissionOffer.cohortId,
    };
}
